import { useCallback, useRef, useState } from "react";

interface UseMultiClickParams {
  isEnabled?: boolean;
  /** Number of consecutive clicks to detect (e.g., 2 for double-click). */
  count: number;
  /** Maximum delay between clicks (e.g., 300ms). */
  delayMs: number;
  /** Callback when N clicks are detected. */
  onMultiClick: (params: { count: number }) => void;
}

interface UseMultiClickResult {
  handleClick: () => void;
}

export function useMultiClick({
  isEnabled = true,
  count,
  delayMs,
  onMultiClick,
}: UseMultiClickParams): UseMultiClickResult {
  const [_, setClickCount] = useState(0);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleClick = useCallback(() => {
    if (!isEnabled) {
      return;
    }

    setClickCount((prev) => {
      const newCount = prev + 1;
      if (newCount >= count) {
        onMultiClick({ count: newCount });
        return 0;
      }
      return newCount;
    });

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      setClickCount(0);
    }, delayMs);
  }, [isEnabled, count, delayMs, onMultiClick]);

  return { handleClick: handleClick };
}
